/**
* This is a TypeGen auto-generated file.
* Any changes made to this file will be lost when the file is regenerated.
*/

import {Observable} from "rxjs";
import {Injectable, Injector} from "@angular/core";
import {HttpEvent} from "@angular/common/http";
import {ApiServiceBase} from "../../api-service-base";
import {CreateExamFlowRequest} from "../model/create-exam-flow-request";
import {DeleteExamFlowRequest} from "../model/delete-exam-flow-request";
import {ExamFlow} from "../model/exam-flow";
import {ExamScheduleSettings} from "../model/exam-schedule-settings";
import {ExamSession} from "../model/exam-session";
import {ListData} from "../model/list-data";
import {ParticipantState} from "../model/participant-state";
import {ScheduledSound} from "../model/scheduled-sound";
import {ScheduledSoundRequest} from "../model/scheduled-sound-request";
import {SearchParamRequest} from "../model/search-param-request";
import {SessionPhaseType} from "../model/session-phase-type";
import {SessionRunParticipantStateType} from "../model/session-run-participant-state-type";
/**
 * Represents a controller for managing exam flows.
 */ 

@Injectable({
    providedIn: "root",
})
export class SimExamFlowService extends ApiServiceBase {
	constructor(injector: Injector) {
        super(injector);
	}
/**
 * Adds an announcement to a specific exam session phase.
 */ 

    public addAnnouncementToExamFlowPhase(examFlowId: string, sessionPhaseType: SessionPhaseType, scheduledSoundRequest: ScheduledSoundRequest) {
        return super.post<ScheduledSound>(`/api/ExamFlow/AddAnnouncementToExamFlowPhase/add-announcement/${encodeURIComponent(examFlowId)}/${encodeURIComponent(sessionPhaseType)}`, scheduledSoundRequest);
    }
/**
 * Closes the confirmation of an exam session. After this phase, no more participants can checkin. Calling the endpoint will generate the ExamFlowState object for the exam session.
 */ 

    public closeConfirmation(examSessionId: string) {
        return super.post<void>(`/api/ExamFlow/CloseConfirmation/${encodeURIComponent(examSessionId)}`, {});
    }
/**
 * Creates a new exam flow.
 */ 

    public createExamFlow(createExamFlowRequest: CreateExamFlowRequest) {
        return super.post<ExamFlow>(`/api/ExamFlow/CreateExamFlow`, createExamFlowRequest);
    }
/**
 * Deletes an exam flow.
 */ 

    public deleteExamFlow(deleteExamFlowRequest: DeleteExamFlowRequest) {
        return super.post<ExamFlow>(`/api/ExamFlow/DeleteExamFlow`, deleteExamFlowRequest);
    }
/**
 * Duplicates an existing exam flow, with a new name.
 */ 

    public duplicateExamFlow(examFlowId: string, newExamFlowName: string) {
        return super.post<ExamFlow>(`/api/ExamFlow/DuplicateExamFlow/duplicate/${encodeURIComponent(examFlowId)}/with-name/${encodeURIComponent(newExamFlowName)}`, {});
    }
/**
 * Retrieves the error test message.
 */ 

    public getErrorTest() {
        return super.get<string>(`/api/ExamFlow/GetErrorTest`, );
    }
/**
 * Retrieves an ExamFlow object by its ID.
 */ 

    public getExamFlowById(examFlowId: string) {
        return super.get<ExamFlow>(`/api/ExamFlow/GetExamFlowById`, {examFlowId: examFlowId});
    }
/**
 * Retrieves a list of ExamSession objects that are influenced by the specified ExamFlow ID.
 */ 

    public getExamSessionsInfluencedByExamFlow(examFlowId: string) {
        return super.get<ExamSession[]>(`/api/ExamFlow/GetExamSessionsInfluencedByExamFlow`, {examFlowId: examFlowId});
    }
/**
 * Retrieves a list of filtered exam flows based on the specified search parameters.
 */ 

    public getFilteredExamFlows(searchParams: SearchParamRequest) {
        return super.get<ListData<ExamFlow>>(`/api/ExamFlow/GetFilteredExamFlows`, {searchParams: searchParams}, ["searchParams"]);
    }
/**
 * Removes an announcement from a specific exam session phase.
 */ 

    public removeAnnouncementOfExamFlowPhase(examFlowId: string, sessionPhaseType: SessionPhaseType, scheduledSoundId: string) {
        return super.post<void>(`/api/ExamFlow/RemoveAnnouncementOfExamFlowPhase/remove-announcement/${encodeURIComponent(examFlowId)}/${encodeURIComponent(sessionPhaseType)}/${encodeURIComponent(scheduledSoundId)}`, {});
    }

    public requestSupportForParticipant(examSessionId: string, participantUserId: string) {
        return super.post<void>(`/api/ExamFlow/RequestSupportForParticipant/${encodeURIComponent(examSessionId)}/participants/${encodeURIComponent(participantUserId)}`, {});
    }

    public resolveSupportRequestForParticipant(examSessionId: string, participantUserId: string) {
        return super.post<void>(`/api/ExamFlow/ResolveSupportRequestForParticipant/${encodeURIComponent(examSessionId)}/participants/${encodeURIComponent(participantUserId)}`, {});
    }

    public setAutoRun(examSessionId: string, autoRunEnabled: boolean) {
        return super.post<void>(`/api/ExamFlow/SetAutoRun/${encodeURIComponent(examSessionId)}`, autoRunEnabled);
    }
/**
 * Set the state of a participant in a run. This is used to control the flow of the exam. Each Participant needs to be ready for the ExamFlow to proceed.
 */ 

    public setParticipantRunState(examSessionId: string, participantUserId: string, participantStateType: SessionRunParticipantStateType) {
        return super.post<void>(`/api/ExamFlow/SetParticipantRunState/${encodeURIComponent(examSessionId)}/participants/${encodeURIComponent(participantUserId)}`, participantStateType);
    }
/**
 * The endpoint should be used to checkin participants for an exam session. Sets the state of a participant.
 */ 

    public setParticipantState(examSessionId: string, participantUserId: string, participantState: ParticipantState) {
        return super.post<void>(`/api/ExamFlow/SetParticipantState/${encodeURIComponent(examSessionId)}/participants/${encodeURIComponent(participantUserId)}`, participantState);
    }
/**
 * Opens the confirmation of an exam session. During this phase, participants can checkin for the exam.
 */ 

    public startConfirmation(examSessionId: string) {
        return super.post<void>(`/api/ExamFlow/StartConfirmation/${encodeURIComponent(examSessionId)}`, {});
    }
/**
 * Starts the first exam run and phase.
 */ 

    public startExamRuns(examSessionId: string) {
        return super.post<ExamSession>(`/api/ExamFlow/StartExamRuns/${encodeURIComponent(examSessionId)}`, {});
    }
/**
 * Updates an existing announcement in a specific exam session phase.
 */ 

    public updateAnnouncementOfExamFlowPhase(examFlowId: string, sessionPhaseType: SessionPhaseType, scheduledSoundId: string, scheduledSoundRequest: ScheduledSoundRequest) {
        return super.post<ScheduledSound>(`/api/ExamFlow/UpdateAnnouncementOfExamFlowPhase/update-announcement/${encodeURIComponent(examFlowId)}/${encodeURIComponent(sessionPhaseType)}/${encodeURIComponent(scheduledSoundId)}`, scheduledSoundRequest);
    }
/**
 * Update the exam schedule settings for an exam session.
 */ 

    public updateExamScheduleSettings(examSessionId: string, examScheduleSettings: ExamScheduleSettings) {
        return super.post<ExamSession>(`/api/ExamFlow/UpdateExamScheduleSettings/${encodeURIComponent(examSessionId)}/schedule-settings`, examScheduleSettings);
    }
/**
 * Upserts an exam flow.
 */ 

    public upsertExamFlow(examFlow: ExamFlow) {
        return super.post<string>(`/api/ExamFlow/UpsertExamFlow`, examFlow);
    }
}